import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useRoute } from '@/composables/common/router/useRouter';

/**
 * Reset password form state and submit.
 *
 * Deps are injectable so the token extraction can be unit tested without a
 * mounted component. When omitted they resolve from the current instance,
 * so this must be called from setup().
 */
export function useResetPassword(deps = {}) {
    const route = deps.route || useRoute();

    const formData = ref({
        email: '',
        password: '',
        password_confirmation: ''
    });

    const errors = ref(null);
    const loading = ref(false);
    const cambiada = ref(false);
    const captchaToken = ref(null);
    const sitekey = process.env.VUE_APP_CLOUDFLARE_SITE_KEY;

    const canSubmit = computed(() => !!captchaToken.value && !loading.value);

    const onCaptchaVerified = token => {
        captchaToken.value = token;
    };

    /**
     * Turnstile tokens expire while the user is still typing and the widget
     * also reports hard errors. Both must invalidate the stored token, or
     * canSubmit would stay true and the form would submit a stale token.
     */
    const onCaptchaInvalid = () => {
        captchaToken.value = null;
    };

    /**
     * Clears the stored token. Called after every failed attempt, because the
     * backend consumes the token and it cannot be reused.
     *
     * Resetting the widget itself is the view's responsibility: this composable
     * must not know the public API of a visual component.
     */
    const clearCaptcha = () => {
        captchaToken.value = null;
    };

    const submit = async() => {
        if (!canSubmit.value) return { ok: false };

        loading.value = true;
        errors.value = null;

        const payload = {
            token: route.query.token,
            ...formData.value,
            filters: {
                tid: store.getters.getConfig && store.getters.getConfig.hash_teatro
            },
            captchaToken: captchaToken.value
        };

        try {
            const res = await store.dispatch(config.getVuexAlias('reset-password'), payload);
            cambiada.value = true;
            return { ok: true, data: res.data };
        } catch (error) {
            // The store rejects two different shapes: an Axios error carries
            // `error.response.data`, while AuthModule rejects the raw result
            // envelope (`error.data`) when the endpoint answers without `auth`.
            // Normalising both here keeps the whole validation envelope (the
            // `errors` key included) visible to the template, which iterates
            // `errors.errors`, whichever path the rejection took.
            const responseData = (error && error.response && error.response.data)
                || (error && error.data)
                || {};
            errors.value = responseData.errors ? responseData : null;

            // Single-use token: the view resets the widget on this failure so
            // the user can retry, but clearing the stored token is ours.
            clearCaptcha();
            return { ok: false, error };
        } finally {
            loading.value = false;
        }
    };

    return {
        formData,
        errors,
        loading,
        cambiada,
        captchaToken,
        sitekey,
        canSubmit,
        onCaptchaVerified,
        onCaptchaInvalid,
        clearCaptcha,
        submit
    };
}
